<template>
  <div class="CaliforniaNotice">
    <h2 class="CaliforniaNotice_Heading">
      PRIVACY NOTICE FOR CALIFORNIA RESIDENTS
    </h2>

    <p>
      If you are a California resident, the CCPA and CPRA grant you certain
      rights related to your Personal Information as summarized below.
    </p>

    <ul class="CalifornaiNotice_BulletPoints">
      <li>
        <span class="CaliforniaNotice_ItemTitle">Right to Know:</span>
        You may ask us for a copy of your Personal Information collected over
        the past 12 months and for information about how we collect, use,
        disclose, and sell it.
      </li>
      <li>
        <span class="CaliforniaNotice_ItemTitle">Right to Deletion:</span>
        You may ask us to delete any Personal Information. If you delete your
        Personal Information, you will permanently lose access to your account
        and the information in your account. We may save Personal Information
        when permitted by applicable law or for business purposes including,
        without limitation, when the information is needed to comply with our
        legal obligations (including law enforcement requests), meet regulatory
        requirements, meet requirements of our business operations, resolve
        disputes, maintain security, prevent fraud and abuse, enforce our Terms
        of Service, fulfill your request to “unsubscribe” from further messages
        from us, or confirm that we have deleted your data. We retain
        de-personalized information after your account has been closed. Any
        information that you posted in product reviews, including your first
        name and last initial will remain visible on the Service. We cannot
        disclose or delete specific pieces of Personal Information if the
        disclosure would create a substantial, articulable, and unreasonable
        risk to the security of the personal information, your account with us
        or the security of our systems.
      </li>
      <li>
        <span class="CaliforniaNotice_ItemTitle">Right to Correction:</span>
        You may modify certain details of your Personal Information by logging
        into our Service and using your username and password and visiting
        “Settings” on the “Account” page. If there are additional details of
        your Personal Information that need to be corrected, you may submit a
        request by completing this
        <SkyLink
          to="https://preferences.grove.co/privacy"
          target="_blank"
          tag="a"
          inline
          >Privacy Request Form</SkyLink
        >
        or contacting
        <SkyLink to="mailto:support@grove.co" inline tag="a"
          >support@grove.co.</SkyLink
        >
      </li>

      <li>
        <span class="CaliforniaNotice_ItemTitle"
          >Right to Request Information:</span
        >
        You have the right to request certain information regarding the Personal
        Information we have collected about you. You may make such a request up
        to twice in a 12- month span. Please note that there are circumstances
        in which we may not be able to comply with your request, including when
        we cannot verify your request or when there is a conflict with our own
        obligations to comply with other legal or regulatory requirements. We
        will notify you following submission of your request, if this is the
        case.
      </li>

      <li>
        <span class="CaliforniaNotice_ItemTitle">
          Notice of Sale and Right to Opt-Out of the Sale or Sharing of Your
          Personal Information:
        </span>
        Grove Collaborative does not sell, in the traditional sense of the word,
        or rent Personal Information to third parties. We do, however, share
        your Personal Information for targeted advertising purposes as we
        describe in the Privacy Policy to make the Service available to you. We
        have shared the categories of Personal Information
        <SkyLink to="#privacy-policy-chart" inline
          >over the past 12 months</SkyLink
        >
        as listed in the Privacy Policy. For example, we may disclose
        information to advertising partners, advertising technology companies,
        and companies that perform advertising-related services in order to
        improve the relevance of our advertising and marketing. To the extent
        possible, Grove Collaborative excludes Personal Information from
        California residents from these activities. We do not knowingly sell or
        share Personal Information of consumers under 16 years of age.You have
        the Right to Opt-Out of sharing of your Personal Information and of the
        processing of Personal Information for the purpose of targeted
        advertising. To exercise this right, please submit a request by clicking
        here. The link will take you to a webform that you can complete and
        submit to make a request to opt out. Please note that any request to
        opt-out may take a few days to become effective, but will be handled
        within the timeframe permitted by law. Once your opt out request is
        processed, it will apply on a going forward basis with respect to our
        disclosure of information. Although we do not sell your Personal
        Information after you submit an “Opt Out Request”, we will continue to
        share some Personal Information with service providers. These service
        providers help us perform a host of services, including, but not limited
        to, analytic-related functions such as measuring our website
        performance, ensuring services are working correctly and securely,
        providing aggregate statistics and analytics, communicating directly
        with users and/or reducing fraud.
      </li>

      <li>
        <span class="CaliforniaNotice_ItemTitle"
          >Right to Limit Use of Sensitive Personal Information:</span
        >
        California consumers have the right to limit the use of each type of
        Sensitive Personal Information for each purpose with each type of
        third-party partner. Consumers can revoke this permission at any time.
        Please note that Grove Collaborative collects very limited Sensitive
        Personal Information from users of the Service and we do not use or
        disclose Sensitive Personal Information in ways that would be subject to
        a California resident’s right to limit the use or disclosure of his or
        her Sensitive Personal Information under the CCPA. Grove Collaborative
        only uses or discloses Sensitive Personal Information to provide to you
        our products and/or Services and related information. If you are a user
        of the Service, Grove collects and stores (i) your account login
        information for the business purpose required (to permit and process
        your account access); and (ii) health related information you choose to
        provide in response to the Grove Wellness quiz to display, email and
        offer product information. If you are a job applicant or employee, much
        of the Personal Information that Grove Collaborative collects and stores
        about you (including your social security number, driver’s license
        number, racial/ethnic origins, immigration status, religious or
        philosophical beliefs, contents of emails or texts to others,
        genetic/biometric data, sexual orientation data, medical information,
        and information related to any accommodation requests regarding
        potential disabilities or other health conditions) include, or contain
        information from which it may be possible to infer, Sensitive Personal
        Information or characteristics of protected classifications under the
        CCPA. At this time, we do not use or disclose Sensitive Personal
        Information in ways that would be subject to a California resident’s
        right to limit the use or disclosure of his or her Sensitive Personal
        Information under the CCPA.
      </li>
      <li>
        <span class="CaliforniaNotice_ItemTitle"
          >Right to Non-Discrimination:</span
        >
        Grove Collaborative will not discriminate against consumers who exercise
        their rights. Specifically, if you exercise your rights, we will not
        deny you access to the Service, charge you different prices or rates for
        products or Service or provide you a different level or quality of
        products or Service. However, as permitted by the CCPA, we may offer you
        certain financial incentives that can result in different prices, rates,
        or quality levels. Any permitted financial incentive we offer will
        reasonably relate to the value of your Personal Information, for
        instance, if you sign up for a newsletter with us, we may provide you
        with discounts for future Services. Participation in a financial
        incentive program requires your prior opt in consent, which you may
        revoke at any time. Please note that a legitimate denial of a request to
        access, delete, or opt-out is not discriminatory, nor is charging a fee
        for excessive or repetitive requests.
      </li>

      <li>
        <span class="CaliforniaNotice_ItemTitle">Right to Appeal:</span>
        You have the right to appeal our decisions about your data subject
        requests.
      </li>
    </ul>

    <p>
      If any of the rights described in the sections above apply to you, you may
      make a request by visiting the
      <SkyLink to="/settings/password-and-privacy" inline tag="a"
        >Password & Privacy section</SkyLink
      >
      of your Account Settings, or contacting us at
      <SkyLink to="mailto:dataprivacy@grove.co" inline tag="a"
        >dataprivacy@grove.co</SkyLink
      >, and we will consider your request in accordance with applicable laws.
      In order to complete your request, you must confirm your identity. If you
      request to opt-out of sales/sharing of your Personal Information under the
      CCPA, you will be directed to verify your identity before completing your
      request. If you choose to exercise another right under the CCPA, you will
      directed to verify your identity by logging into your account.
    </p>

    <h3 id="california-residents" class="CaliforniaNotice_Subheading">
      California “Shine the Light” disclosure
    </h3>
    <p>
      The California “Shine the Light” law gives residents of California the
      right under certain circumstances to opt out of the sharing of certain
      categories of personal information (as defined in the Shine the Light law)
      with third parties for their direct marketing purposes, or in the
      alternative, that we provide a cost-free means for consumers to opt out of
      any such sharing. We do not currently share your Personal Information with
      third parties for their own direct marketing purposes.
    </p>
  </div>
</template>

<style lang="scss">
.CaliforniaNotice {
  display: grid;
  row-gap: var(--spacing-3x);

  &_Heading {
    @include type-brand-heading('2');
    margin-top: var(--spacing-8x);
    text-align: center;
  }

  &_Subheading {
    @include type-brand-heading('3');
    margin-top: var(--spacing-6x);
  }

  &_ItemTitle {
    text-decoration: underline;
  }

  &_BulletPoints {
    margin: 0;
    display: grid;
    row-gap: var(--spacing-2x);
  }
}
</style>
